import { NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { GalleryAccessLevel, GalleryDto } from '@picflow/types';
import { GalleryInfoDialogComponent } from 'src/app/gallery/dialogs/gallery-info-dialog/gallery-info-dialog.component';
import { getFullPath } from 'src/app/lib/get-full-path';
import { sleep } from 'src/app/lib/sleep';
import { sortSubGalleries } from 'src/app/lib/sort-subgalleries';
import { swapGalleryInUrl } from 'src/app/lib/swap-gallery-in-url';
import { AccessLevelService } from 'src/app/services/access-level.service';
import { DialogService } from 'src/app/services/dialog.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { PermissionService } from 'src/app/services/permission.service';
import { SectionService } from 'src/app/services/section.service';
import { ProTagComponent } from '../../../shared-extra/components/pro-tag/pro-tag.component';
import { PermissionsPipe } from '../../../shared-extra/pipes/permissions.pipe';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';

@Component({
  selector: 'app-gallery-tree',
  templateUrl: './gallery-tree.component.html',
  styleUrls: ['./gallery-tree.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    ButtonDirective,
    RouterLink,
    TooltipDirective,
    SvgComponent,
    NgFor,
    ProTagComponent,
    LoadingIndicatorComponent,
    PermissionsPipe,
    GalleryTeaserUrlPipe,
  ],
})
export class GalleryTreeComponent implements OnInit {
  @Input() gallery: GalleryDto;
  @Input() clientMode: boolean;
  @Output() onAddedGallery = new EventEmitter();
  @Output() onClose = new EventEmitter();
  parentGallery: GalleryDto;
  subGalleries: GalleryDto[];
  links: { [galleryId: string]: string[] } = {};
  accessLevel: GalleryAccessLevel;
  loading = false;
  noAccess = false;

  constructor(
    private galleryService: GalleryService,
    private route: ActivatedRoute,
    private router: Router,
    private permissionService: PermissionService,
    private sectionService: SectionService,
    private accessLevelService: AccessLevelService,
    private dialog: DialogService
  ) {}

  async ngOnInit() {
    if (this.gallery['parent_gallery_id']) {
      try {
        this.parentGallery = await this.galleryService.getGallery(this.gallery['parent_gallery_id'], {
          ignore401Errors: true,
        });
      } catch {
        this.noAccess = true;
        return;
      }
    } else {
      this.parentGallery = this.gallery;
    }

    this.accessLevel = await this.accessLevelService.getAccessLevel(this.parentGallery);
    const resp = await this.galleryService.getSubGalleries(this.parentGallery['id']);
    const sections = await this.sectionService.getSections(this.parentGallery['id']);

    this.subGalleries = [];

    sections
      .filter(section => section.object === 'gallery')
      .forEach(section => {
        const sectionGalleries = resp.filter(gallery => gallery.section === section.id);
        this.subGalleries.push(...sortSubGalleries(sectionGalleries, section.sorting));
      });

    this.createLinks();
  }

  async addSubGallery(e: Event) {
    if (!this.permissionService.canDisplayElement('gallery.subGallery', this.parentGallery)) {
      return this.router.navigate(['a', 'pricing']);
    }

    e.preventDefault();
    e.stopPropagation();
    if (this.loading) return;
    this.loading = true;
    const gallery = await this.galleryService.createSubGallery(this.parentGallery);
    this.subGalleries.push(gallery);
    this.createLinks();
    this.loading = false;
    this.onAddedGallery.emit();
    await sleep(250);
    document.querySelector('app-edit-album-section')?.scrollIntoView();
  }

  openGalleryInfo(gallery: GalleryDto, event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.dialog.open(GalleryInfoDialogComponent, { gallery, inGallery: true });
  }

  private createLinks() {
    const path = getFullPath(this.route.snapshot);
    this.links = {};
    this.links[this.parentGallery['id']] = swapGalleryInUrl(this.gallery, this.parentGallery, path, !this.clientMode);
    this.subGalleries.forEach(
      gallery => (this.links[gallery.id] = swapGalleryInUrl(this.gallery, gallery, path, !this.clientMode))
    );
  }
}
